import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const SERVER_RESTARTING_MSG = "Server is restarting, please retry in a moment.";

// A request failed because the backend is momentarily unavailable (gateway errors,
// network drop, or timeout) rather than a real application error.
export function isServerUnavailable(e) {
  const status = e?.response?.status;
  if (status === 502 || status === 503 || status === 504) return true;
  // No HTTP response at all -> network error / connection refused / DNS
  if (!e?.response && (e?.request || e?.code === "ERR_NETWORK" || e?.code === "ECONNABORTED" || e?.message === "Network Error")) return true;
  return false;
}

export function apiError(e) {
  if (isServerUnavailable(e)) return (e && e.__retryExhausted) ? "Still unreachable - please retry." : SERVER_RESTARTING_MSG;
  return formatApiErrorDetail(e?.response?.data?.detail) || e?.message || "Request failed";
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Broadcast reconnect state so a global banner can show/hide during auto-retries.
function emitReconnect(active) {
  try { window.dispatchEvent(new CustomEvent("sgex:reconnecting", { detail: { active } })); } catch (e) { /* noop */ }
}

// Run an async request and automatically retry ONCE (after `delay` ms) when the
// backend is briefly unavailable (502/503/504/network) - e.g. a dev hot-reload.
export async function retryRequest(fn, { retries = 1, delay = 2000 } = {}) {
  let attempt = 0;
  try {
    while (true) {
      try {
        return await fn();
      } catch (e) {
        if (attempt < retries && isServerUnavailable(e)) {
          attempt += 1;
          emitReconnect(true);
          await sleep(delay);
          continue;
        }
        if (isServerUnavailable(e)) { try { e.__retryExhausted = true; } catch (_) { /* noop */ } }
        throw e;
      }
    }
  } finally {
    if (attempt > 0) emitReconnect(false);
  }
}

export async function downloadBlob(path, filename) {
  const res = await api.get(path, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
